import { useMemo } from 'react';
import { Activity, AlertTriangle, ShieldAlert, Truck } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import DataTable from '@/components/data-table/data-table';
import { StatCard } from '@/components/stat-card';

import { formatDateTime, formatNumber } from '@/utils/format';

import type {
  Incident,
  IncidentKind,
  IncidentSeverity,
  IncidentStatus,
} from '@/features/monitoring/monitoring.types';
import type { ColumnDef } from '@tanstack/react-table';

import { useMonitoringPage } from './use-monitoring-page';

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
};

const SEVERITY_BADGE_CLASS: Record<IncidentSeverity, string> = {
  low: 'badge-ghost',
  medium: 'badge-info',
  high: 'badge-warning',
  critical: 'badge-error',
};

const KIND_LABEL: Record<IncidentKind, string> = {
  offline: 'Punto fuera de línea',
  capacity: 'Capacidad excedida',
  delay: 'Retraso operativo',
  sync_error: 'Error de sincronización',
};

const STATUS_LABEL: Record<IncidentStatus, string> = {
  open: 'Abierta',
  investigating: 'En revisión',
  resolved: 'Resuelta',
};

const STATUS_BADGE_CLASS: Record<IncidentStatus, string> = {
  open: 'badge-error',
  investigating: 'badge-warning',
  resolved: 'badge-success',
};

/**
 * Monitoring dashboard view.
 * Shows the live operational health summary and the incidents feed.
 */
export function MonitoringPage() {
  const { summary, incidents, isLoading, isError } = useMonitoringPage();

  const columns = useMemo<ColumnDef<Incident>[]>(
    () => [
      {
        accessorKey: 'dispatch_place_name',
        header: 'Punto de despacho',
        cell: (info) => <span className="font-semibold">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'country_code',
        header: 'País',
        size: 90,
        meta: { align: 'center' },
      },
      {
        accessorKey: 'kind',
        header: 'Tipo de incidencia',
        cell: (info) => KIND_LABEL[info.getValue() as IncidentKind],
      },
      {
        accessorKey: 'severity',
        header: 'Severidad',
        size: 120,
        cell: (info) => {
          const severity = info.getValue() as IncidentSeverity;
          return (
            <span className={twMerge('badge', SEVERITY_BADGE_CLASS[severity])}>
              {SEVERITY_LABEL[severity]}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        size: 130,
        cell: (info) => {
          const status = info.getValue() as IncidentStatus;
          return (
            <span className={twMerge('badge', STATUS_BADGE_CLASS[status])}>
              {STATUS_LABEL[status]}
            </span>
          );
        },
      },
      {
        accessorKey: 'detected_at',
        header: 'Detectada',
        size: 150,
        cell: (info) => formatDateTime(info.getValue() as string),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <div className="flex justify-center p-8">
        <div className="alert alert-error max-w-lg">
          <span>No se pudo cargar el monitoreo. Intenta de nuevo.</span>
        </div>
      </div>
    );
  }

  const onlineHint = summary
    ? `de ${formatNumber(summary.dispatch_places_total)} totales`
    : undefined;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold">Monitoreo</h1>
        <p className="text-base-content/70">
          Estado en vivo de la red de puntos de despacho e incidencias activas.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Puntos en línea"
          value={formatNumber(summary?.dispatch_places_online ?? 0)}
          hint={onlineHint}
          Icon={Activity}
          tone="success"
        />
        <StatCard
          label="Órdenes en tránsito"
          value={formatNumber(summary?.orders_in_transit ?? 0)}
          Icon={Truck}
          tone="info"
        />
        <StatCard
          label="Incidencias abiertas"
          value={formatNumber(summary?.open_incidents ?? 0)}
          Icon={AlertTriangle}
          tone="warning"
        />
        <StatCard
          label="Alertas críticas"
          value={formatNumber(summary?.critical_alerts ?? 0)}
          Icon={ShieldAlert}
          tone="error"
        />
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-semibold">Incidencias recientes</h2>
        <DataTable columns={columns} data={incidents} isLoading={isLoading} />
      </section>
    </div>
  );
}
